import { ClipboardList, CalendarDays, Heart, Cross, Music, Building2, Sparkles, FileText, Lightbulb, CheckCircle2, Clock, PhoneCall, Users, Baby, Headphones, MapPin, Euro, Shield, Star, Flame, Mic2, PartyPopper, Wrench, Drama, Wand2 } from 'lucide-react';
import React from 'react';

export interface FAQCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: { question: string; answer: React.ReactNode }[];
}

export const faqCategories: FAQCategory[] = [
  {
    id: 'general',
    label: "L'agence & premier contact",
    icon: <ClipboardList size={16} />,
    items: [
      {
        question: "Qu'est-ce que Cygnature Production et quelles sont ses spécialités ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Cygnature Production est une agence événementielle haut de gamme</strong> basée à Saint-Pouange (Aube, 10), fondée par Cyrille Lecourt. L'agence propose une offre tout-en-un : organisation d'événements publics et privés, production artistique, wedding planning, cérémonies laïques, animations enfants, musique live et location de matériel.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { value: '250+', label: 'Événements' },
                { value: '25 ans', label: "D'expérience" },
                { value: '100+', label: 'Artistes' },
                { value: '100%', label: 'Sur-mesure' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl border border-border bg-card p-3 text-center">
                  <p className="font-heading font-bold text-primary text-lg">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        question: "Dans quelles villes et régions Cygnature Production intervient-elle ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Zone principale : <strong className="text-foreground">Troyes et l'Aube (10)</strong>. Intervention régulière dans le Grand Est, la Bourgogne, l'Île-de-France et toute la France pour les événements d'envergure.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Pages dédiées pour : Reims, Châlons-en-Champagne, Auxerre, Sens, Romilly-sur-Seine, La Chapelle-Saint-Luc, Saint-André-les-Vergers, Sainte-Savine, Nogent-sur-Seine et Bar-sur-Seine.
            </p>
          </div>
        ),
      },
      {
        question: "Qui est Cyrille Lecourt, fondateur de Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Cyrille Lecourt a un <strong className="text-foreground">parcours atypique et inspirant</strong> : touché par un cancer rarissime à 3 ans, il a perdu un œil mais forgé une résilience hors du commun. Avec <strong className="text-foreground">plus de 25 ans d'expérience</strong> dont plusieurs années à l'E.S. Troyes A.C., il s'est construit une réputation solide dans l'événementiel.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ses clients le surnomment <em>« Le Ninja de l'événementiel »</em> et <em>« La Pieuvre aux 8 bras »</em>. Sa philosophie : <em>« Je prends place à côté de mes clients »</em>.
            </p>
          </div>
        ),
      },
      {
        question: "Comment contacter Cygnature Production pour un premier échange ?",
        answer: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border bg-card p-4 text-center">
                <PhoneCall size={20} className="text-primary mx-auto mb-2" />
                <p className="font-heading font-bold text-foreground text-sm">Téléphone</p>
                <p className="text-xs text-muted-foreground">Échange direct</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 text-center">
                <FileText size={20} className="text-primary mx-auto mb-2" />
                <p className="font-heading font-bold text-foreground text-sm">Formulaire</p>
                <p className="text-xs text-muted-foreground">Sur le site</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 text-center">
                <CalendarDays size={20} className="text-primary mx-auto mb-2" />
                <p className="font-heading font-bold text-foreground text-sm">RDV gratuit</p>
                <p className="text-xs text-muted-foreground">Sans engagement</p>
              </div>
            </div>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Lightbulb size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Votre première consultation est offerte</strong> — parlons de votre projet en toute liberté. Délai de réponse garanti sous 48 à 72h.
              </p>
            </div>
          </div>
        ),
      },
      {
        question: "Quels sont les tarifs de Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Notre tarification est <strong className="text-foreground">100% sur-mesure</strong> car chaque projet est unique. Le prix dépend de la nature de l'événement, du nombre d'invités, des prestations choisies, de la complexité logistique et des délais.
            </p>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Euro size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Un budget maîtrisé, des événements inoubliables.</strong> Un devis détaillé et transparent est fourni après le premier échange.
              </p>
            </div>
          </div>
        ),
      },
      {
        question: "Combien de temps à l'avance faut-il contacter Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-3 font-heading font-semibold text-foreground">Type d'événement</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold text-foreground">Délai recommandé</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Mariage', '12 à 18 mois'],
                    ['Gala / séminaire', '6 à 12 mois'],
                    ['Anniversaire / fête privée', '3 à 6 mois'],
                    ['Événement corporate urgent', 'Délais réduits possibles'],
                  ].map(([type, delai], i) => (
                    <tr key={type} className={`border-t border-border ${i % 2 ? 'bg-muted/20' : ''}`}>
                      <td className="px-4 py-3 text-muted-foreground">{type}</td>
                      <td className="px-4 py-3 text-muted-foreground font-semibold">{delai}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Clock size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Besoin en urgence ?</strong> La réactivité de Cyrille est légendaire — contactez-nous pour vérifier nos disponibilités.
              </p>
            </div>
          </div>
        ),
      },
      {
        question: "Cygnature Production travaille-t-elle avec des prestataires locaux de l'Aube ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui, nous collaborons avec un <strong className="text-foreground">réseau de plus de 100 prestataires sélectionnés avec exigence</strong> : traiteurs, photographes, domaines, châteaux et vidéastes. La priorité est donnée aux acteurs locaux de l'Aube et du Grand Est.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              L'avantage pour vous : un réseau éprouvé, des tarifs négociés et une cohérence qualité garantie.
            </p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce qui différencie Cygnature Production des autres agences événementielles ?",
        answer: (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { title: 'Interlocuteur unique', desc: 'Cyrille gère tout de A à Z, un seul contact' },
                { title: '25 ans d\'expérience', desc: '250+ événements réussis, références solides' },
                { title: 'Offre tout-en-un', desc: 'Events, artistes, wedding, cérémonies, animations' },
                { title: 'Sur-mesure absolu', desc: 'Aucun copier-coller, chaque projet est unique' },
              ].map((item) => (
                <div key={item.title} className="rounded-xl border border-border bg-card p-4">
                  <p className="font-heading font-semibold text-foreground text-sm">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'evenements',
    label: 'Événements & logistique',
    icon: <CalendarDays size={16} />,
    items: [
      {
        question: "Quels types d'événements Cygnature Production organise-t-elle ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Nous organisons une <strong className="text-foreground">très large variété d'événements</strong> :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <Heart size={18} className="text-primary mb-2" />
                <p className="font-heading font-semibold text-foreground text-sm">Événements privés</p>
                <p className="text-xs text-muted-foreground mt-1">Mariages, anniversaires, fêtes familiales, réceptions</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <Building2 size={18} className="text-primary mb-2" />
                <p className="font-heading font-semibold text-foreground text-sm">Événements professionnels</p>
                <p className="text-xs text-muted-foreground mt-1">Séminaires, team building, galas, inaugurations</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <PartyPopper size={18} className="text-primary mb-2" />
                <p className="font-heading font-semibold text-foreground text-sm">Événements publics</p>
                <p className="text-xs text-muted-foreground mt-1">Festivals, animations de ville, fêtes de village</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <Star size={18} className="text-primary mb-2" />
                <p className="font-heading font-semibold text-foreground text-sm">Événements sportifs</p>
                <p className="text-xs text-muted-foreground mt-1">Compétitions, journées portes ouvertes, soirées</p>
              </div>
            </div>
          </div>
        ),
      },
      {
        question: "En quoi consiste la prise en charge 'de A à Z' d'un événement ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Notre accompagnement couvre <strong className="text-foreground">chaque étape de votre projet</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Écoute et brief — comprendre votre vision',
                'Concept et scénographie — créer l\'univers',
                'Recherche de lieux et prestataires — les meilleurs choix',
                'Coordination logistique — tout planifier',
                'Gestion budgétaire — transparence totale',
                'Coordination jour J — présence sur place garantie',
                'Bilan post-événement — retour et amélioration',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed italic">
              Résultat : <strong className="text-foreground">zéro stress</strong> pour vous, du premier appel au dernier au revoir.
            </p>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle s'occuper uniquement d'une partie de l'événement ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui, nous proposons des <strong className="text-foreground">formules « à la carte »</strong> : uniquement la recherche d'artistes, uniquement la coordination jour J, ou uniquement la scénographie et la décoration. Cygnature s'adapte au niveau d'intervention souhaité.
            </p>
          </div>
        ),
      },
      {
        question: "Comment se déroule le premier rendez-vous de projet ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le premier rendez-vous est <strong className="text-foreground">gratuit et sans engagement</strong>. Il peut se faire en présentiel (Troyes/Aube) ou en visio. L'objectif : comprendre votre projet, vos envies, votre budget et vos contraintes.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              À l'issue, vous recevez une proposition créative et un devis personnalisé sous 48 à 72h.
            </p>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Lightbulb size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Aucune obligation</strong> — juste l'envie de réaliser votre vision.
              </p>
            </div>
          </div>
        ),
      },
      {
        question: "Quels lieux Cygnature Production propose-t-elle dans l'Aube et le Grand Est ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Nous disposons d'un <strong className="text-foreground">réseau de lieux partenaires</strong> variés : domaines et châteaux, salles de réception, espaces atypiques et plein air. Tous sont sélectionnés pour leur qualité et leurs tarifs négociés.
            </p>
          </div>
        ),
      },
      {
        question: "Cygnature Production gère-t-elle les événements en plein air ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui, avec une <strong className="text-foreground">anticipation complète des contraintes</strong> : autorisations préfectorales, plans de sécurité, structures temporaires (chapiteaux, tentes) et gestion météo avec plans B. Notre expérience des festivals et événements outdoor dans l'Aube est un atout majeur.
            </p>
          </div>
        ),
      },
      {
        question: "Puis-je avoir un budget réduit et quand même faire appel à Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Absolument ! Cyrille est le <strong className="text-foreground">MacGyver de l'événementiel</strong> : il trouve des solutions créatives à budget maîtrisé. Parmi les leviers : prestataires locaux, décoration DIY guidée, mutualisation des ressources.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              L'optimisation budgétaire est l'une des forces de Cygnature — on ne sacrifie jamais la qualité, on l'adapte.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'mariage',
    label: 'Mariage & Wedding Planning',
    icon: <Heart size={16} />,
    items: [
      {
        question: "Qu'est-ce qu'un wedding planner et pourquoi en engager un ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un Wedding Planner est un <strong className="text-foreground">coordinateur global de votre mariage</strong> : budget, prestataires, logistique et jour J. C'est un chef d'orchestre ET un soutien émotionnel.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: '200h', label: 'De démarches économisées' },
                { value: '100+', label: 'Prestataires dans le réseau' },
                { value: '0', label: 'Stress le jour J' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl border border-border bg-card p-3 text-center">
                  <p className="font-heading font-bold text-primary text-lg">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        question: "Quelles formules de wedding planning propose Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Nous proposons <strong className="text-foreground">3 niveaux de service</strong> :
            </p>
            <ul className="space-y-2">
              {[
                { title: 'Coordination jour J', desc: 'Vous préparez, Cygnature gère le jour J' },
                { title: 'Assistance partielle', desc: 'Quelques mois d\'accompagnement ciblé' },
                { title: 'Prise en charge complète', desc: 'Du concept à la lune de miel, de A à Z' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground"><strong className="text-foreground">{item.title}</strong> — {item.desc}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground italic">Chaque formule est adaptée à vos besoins et votre budget.</p>
          </div>
        ),
      },
      {
        question: "Cyrille Lecourt est-il vraiment l'un des rares hommes wedding planners en France ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — Cyrille fait partie des <strong className="text-foreground">5% d'hommes wedding planners en France</strong>. Ce regard différent apporte une complémentarité précieuse : attention aux détails « côté marié », gestion logistique et technique sans faille, et 25 ans d'expérience terrain.
            </p>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 text-center">
              <p className="font-heading font-bold text-primary text-2xl">1 sur 20</p>
              <p className="text-xs text-muted-foreground mt-1">wedding planners en France est un homme</p>
            </div>
          </div>
        ),
      },
      {
        question: "Comment se passe l'organisation d'un mariage avec Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un processus clair en <strong className="text-foreground">6 étapes</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Premier RDV découverte (J-18 mois)',
                'Recherche du lieu et des prestataires',
                'Suivi des contrats et paiements',
                'Scénographie et décoration',
                'Répétition et coordination J-1',
                'Coordination complète jour J',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-heading font-bold shrink-0">{i + 1}</span>
                  <span className="text-sm text-muted-foreground">{step}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed italic">
              Cyrille est présent à chaque étape, de la première idée au dernier confetti.
            </p>
          </div>
        ),
      },
      {
        question: "Cygnature Production propose-t-elle du wedding design et de la scénographie ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — Cyrille est aussi <strong className="text-foreground">Wedding Designer</strong> : création de l'univers visuel complet (moodboard, palette de couleurs, décoration sur-mesure, scénographie des espaces). Pas de location catalogue : chaque décoration est pensée spécialement pour le couple.
            </p>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle organiser un mariage dans un château de l'Aube ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui, nous disposons d'un <strong className="text-foreground">réseau de châteaux et domaines partenaires</strong> dans l'Aube et le Grand Est. Avantages : tarifs négociés, connaissance des contraintes techniques de chaque lieu, et coordination avec les équipes des domaines.
            </p>
          </div>
        ),
      },
      {
        question: "Peut-on organiser un mariage laïque et un mariage civil avec Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Distinction importante :</strong> le mariage civil se déroule à la mairie (Cygnature coordonne mais ne célèbre pas), tandis que la cérémonie laïque est personnalisée et Cyrille peut officier lui-même.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              L'avantage unique : <strong className="text-foreground">un seul interlocuteur pour les deux</strong>. Consultez notre section Cérémonie Laïque pour en savoir plus.
            </p>
          </div>
        ),
      },
      {
        question: "Combien coûte un wedding planner dans l'Aube ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Fourchette de marché : de <strong className="text-foreground">1 500 € (coordination jour J) à 8-12% du budget total</strong> (prise en charge complète). Cygnature adapte selon la formule choisie.
            </p>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Euro size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                Le wedding planner est un <strong className="text-foreground">investissement, pas un coût</strong> : les économies réalisées via le réseau prestataires + la valeur du temps gagné le prouvent.
              </p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'ceremonie',
    label: 'Cérémonie laïque',
    icon: <Sparkles size={16} />,
    items: [
      {
        question: "Qu'est-ce qu'une cérémonie laïque et en quoi diffère-t-elle d'un mariage religieux ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Une cérémonie laïque est une <strong className="text-foreground">célébration non religieuse, entièrement personnalisée</strong>, guidée par un officiant. Elle ne remplace pas la cérémonie civile à la mairie mais la complète.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ses avantages : liberté totale du contenu, durée modulable, rituels symboliques choisis par les mariés.
            </p>
          </div>
        ),
      },
      {
        question: "Cyrille Lecourt peut-il officier lors de notre cérémonie laïque ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — Cyrille est <strong className="text-foreground">officiant de cérémonies laïques expérimenté</strong>. Il rédige un texte sur-mesure après plusieurs échanges avec le couple (histoire d'amour, valeurs, moments forts). Il guide la cérémonie avec émotion et professionnalisme.
            </p>
          </div>
        ),
      },
      {
        question: "Quels rituels symboliques peut-on intégrer dans une cérémonie laïque ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Les rituels que nous proposons :
            </p>
            <ul className="space-y-2">
              {[
                'Le sable des amoureux — l\'union de deux familles',
                'La bougie de l\'union — la chaleur du foyer commun',
                'L\'arbre à souhaits — planter les racines du futur',
                'Le lâcher de lanternes (si autorisé)',
                'Le rituel de la lumière — illuminer l\'avenir ensemble',
                'L\'échange de lettres — mots du cœur',
                'La participation des invités — un moment collectif',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Sparkles size={14} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground italic">Choisissez les rituels qui vous ressemblent — nous vous guidons dans le choix.</p>
          </div>
        ),
      },
      {
        question: "Comment se prépare une cérémonie laïque avec Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un processus en <strong className="text-foreground">4 étapes</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Entretien découverte du couple (histoire, valeurs, ambiance)',
                'Rédaction du texte et validation',
                'Répétition de la cérémonie (J-1 recommandé)',
                'Officiation le jour J',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-heading font-bold shrink-0">{i + 1}</span>
                  <span className="text-sm text-muted-foreground">{step}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">Délai recommandé : 6 à 12 mois avant le jour J.</p>
          </div>
        ),
      },
      {
        question: "Une cérémonie laïque a-t-elle une valeur juridique ?",
        answer: (
          <div className="space-y-4">
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Shield size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Non</strong> — la cérémonie laïque est symbolique et n'a pas de valeur légale. Elle doit être couplée avec le mariage civil à la mairie. Cygnature coordonne les deux.
              </p>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed italic">
              La cérémonie laïque complète le mariage civil — elle ne le remplace pas.
            </p>
          </div>
        ),
      },
      {
        question: "Peut-on avoir une cérémonie laïque dans un jardin, un château ou en plein air ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui, c'est même l'un des <strong className="text-foreground">grands avantages de la cérémonie laïque</strong> : totale liberté de lieu. Exemples : jardin privatif, château, bord de lac, vigne champenoise, forêt.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Cygnature gère tous les aspects pratiques : sonorisation, décoration, placement des invités.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'artistes',
    label: 'Artistes & animations',
    icon: <Music size={16} />,
    items: [
      {
        question: "Quels types d'artistes Cygnature Production propose-t-elle ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Notre catalogue couvre <strong className="text-foreground">tous les univers artistiques</strong> avec plus de 100 artistes :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: 'Drama', title: 'Scène & humour', desc: 'Comédiens, humoristes, stand-up' },
                { icon: 'Wand2', title: 'Magie & illusion', desc: 'Magiciens, mentalistes, hypnotiseurs' },
                { icon: 'Music', title: 'Danse & performance', desc: 'Danseurs, performers LED, cabaret' },
                { icon: 'Flame', title: 'Feu & cirque', desc: 'Cracheurs de feu, jongleurs, acrobates' },
              ].map((item) => {
                const IconMap: Record<string, any> = { Drama, Wand2, Music, Flame };
                const Icon = IconMap[item.icon];
                return (
                  <div key={item.title} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={16} className="text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-heading font-semibold text-foreground text-sm">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle fournir de la musique live pour mon événement ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — notre offre musicale est complète :
            </p>
            <ul className="space-y-2">
              {[
                'DJ professionnel',
                'Musiciens live (saxophone, violon, percussions, guitare)',
                'Groupes pop/jazz/rock/variété',
                'Tribute bands',
                'Groupe de cérémonie laïque',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Headphones size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed italic">Adaptation au style de votre événement et de votre public.</p>
          </div>
        ),
      },
      {
        question: "Quelles animations pour un événement d'entreprise (team building, séminaire) ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Nos <strong className="text-foreground">animations corporate spécifiques</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Escape game sur-mesure',
                'Jeux en bois géants',
                'Quiz et olympiades',
                'Animations culinaires',
                'Chasse au trésor',
                'Ateliers créatifs',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Lightbulb size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">L'animation parfaite pour chaque objectif RH</strong> : cohésion d'équipe, challenge ou fun.
              </p>
            </div>
          </div>
        ),
      },
      {
        question: "Quelles animations Cygnature Production propose-t-elle pour les enfants ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Notre offre enfants, adaptée de <strong className="text-foreground">3 à 12 ans</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Maquillage artistique et mascottes',
                'Structures gonflables',
                'Ateliers créatifs et chasse au trésor',
                'Spectacles de magie et cirque enfants',
                'Marionnettes et sculpture de ballons',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Baby size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed italic">Sécurité et professionnalisme garantis pour tous les animateurs.</p>
          </div>
        ),
      },
      {
        question: "Cygnature Production propose-t-elle des animations high-tech et nouvelles technologies ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — notre offre tech :
            </p>
            <ul className="space-y-2">
              {[
                'Réalité virtuelle (VR)',
                'Bornes photo 360°',
                'Photo booth personnalisé',
                'Mappings lumineux',
                'Drones (photo/vidéo)',
                'Livres d\'or numériques',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Sparkles size={14} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed italic">L'événementiel de demain, disponible aujourd'hui dans l'Aube.</p>
          </div>
        ),
      },
      {
        question: "Peut-on organiser un feu d'artifice ou un spectacle pyrotechnique ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui, avec <strong className="text-foreground">toutes les autorisations requises</strong> (préfectorales, sécurité). Cygnature coordonne les prestataires agréés pour des spectacles pyrotechniques personnalisés (couleurs, durée, musique synchronisée).
            </p>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Shield size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Tous nos spectacles pyrotechniques respectent la réglementation en vigueur.</strong>
              </p>
            </div>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle fournir du matériel de son et lumière ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — notre service location comprend :
            </p>
            <ul className="space-y-2">
              {[
                'Sono professionnelle',
                'Éclairages scéniques',
                'Machines à effets (fumée, bulles, confettis)',
                'Machines à popcorn et barbe à papa',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Wrench size={14} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">Livraison et installation possibles pour petits et grands événements.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'corporate',
    label: 'Corporate & entreprise',
    icon: <Building2 size={16} />,
    items: [
      {
        question: "Cygnature Production organise-t-elle des événements pour les entreprises ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — notre <strong className="text-foreground">offre corporate est complète</strong> : séminaires, galas d'entreprise, conventions, soirées de fin d'année, lancements de produits, inaugurations et journées portes ouvertes.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Références : <strong className="text-foreground">Andra, RE-Cycle, E.Leclerc Troyes, 3 Média, Domaine de la Forêt d'Orient…</strong> Confidentialité et professionnalisme garantis.
            </p>
          </div>
        ),
      },
      {
        question: "Comment Cygnature Production gère-t-elle les contraintes de l'événementiel corporate ?",
        answer: (
          <div className="space-y-4">
            <ul className="space-y-2">
              {[
                'Gestion des budgets avec justificatifs',
                'Interlocuteur unique pour les acheteurs',
                'Respect des délais et des procédures',
                'Gestion des invitations et RSVP',
                'Confidentialité garantie',
                'Reporting post-événement disponible',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle organiser un team building original dans l'Aube ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — nos <strong className="text-foreground">team building créatifs</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Escape game thématique',
                'Rallye découverte des vignes de Champagne',
                'Olympiades et jeux géants',
                'Ateliers gastronomiques (dégustation Champagne)',
                'Activités outdoor et chasses au trésor',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Star size={14} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">Adaptés de 10 à 500+ participants.</p>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle gérer des événements de grande envergure (500+ personnes) ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — notre expérience couvre la <strong className="text-foreground">logistique complexe</strong> : gestion de plusieurs espaces simultanés, coordination multi-prestataires, sécurité et accueil. Notre réseau permet des événements jusqu'à plusieurs milliers de personnes.
            </p>
          </div>
        ),
      },
      {
        question: "Puis-je obtenir une facture et un devis pour un événement d'entreprise ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Bien sûr. <strong className="text-foreground">Devis détaillé fourni gratuitement</strong> après le brief. Facturation professionnelle avec TVA, suivi budgétaire et paiement en plusieurs fois possible selon les formules. Cygnature est une structure professionnelle déclarée.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'experiences',
    label: 'Expériences spéciales',
    icon: <Sparkles size={16} />,
    items: [
      {
        question: "Qu'est-ce qu'une expérience immersive et comment Cygnature la propose-t-elle ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Les expériences immersives sont des événements où <strong className="text-foreground">les invités ne sont plus spectateurs mais acteurs</strong>. Exemples : dîner mystère, soirée jeu de rôle grandeur nature, escape experience thématique, aventure sensorielle.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Cygnature conçoit le scénario sur-mesure pour chaque événement.
            </p>
          </div>
        ),
      },
      {
        question: "Cygnature Production propose-t-elle des animations de bar (barman jongleur, cocktails) ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — notre animation bar originale inclut : <strong className="text-foreground">barman jongleur flair, bar à cocktails thématique, bar à champagne</strong> (région oblige !), et mocktails pour les non-alcoolisés. Animation spectacle + service bar, parfait pour les cocktails dînatoires.
            </p>
          </div>
        ),
      },
      {
        question: "Peut-on organiser une soirée à thème complète avec Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — soirées à thème <strong className="text-foreground">clé en main</strong> : définition du thème, scénographie, costumes conseillés, animations cohérentes, musique d'ambiance, décoration et photobooth thématique.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {['Gatsby', 'Vegas', 'Bal masqué', 'Casino', 'Années 80', 'Bollywood', 'Champagne & Luxe'].map((theme) => (
                <span key={theme} className="inline-flex items-center px-3 py-1 rounded-full border border-border text-xs font-heading font-medium text-muted-foreground bg-card">
                  {theme}
                </span>
              ))}
            </div>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle organiser un lâcher de lanternes ou de ballons ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Les <strong className="text-foreground">lanternes sont soumises à réglementation</strong> (certaines zones interdites, risque incendie). Cygnature vérifie la faisabilité et obtient les autorisations si nécessaire. Alternative écologique : lanternes biodégradables ou lâcher de pétales.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'pratique',
    label: 'Réservation & organisation',
    icon: <FileText size={16} />,
    items: [
      {
        question: "Quelles informations préparer avant de contacter Cygnature Production ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Voici une <strong className="text-foreground">checklist pour préparer votre premier échange</strong> :
            </p>
            <ul className="space-y-2">
              {[
                'Date envisagée',
                'Type d\'événement',
                'Nombre d\'invités approximatif',
                'Lieu (ou recherche en cours)',
                'Budget global (fourchette)',
                'Inspirations ou ambiance souhaitée',
                'Coordonnées',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground italic">Même sans tout avoir défini, le premier échange suffit pour démarrer.</p>
          </div>
        ),
      },
      {
        question: "Cygnature Production peut-elle intervenir en urgence pour un événement de dernière minute ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Selon disponibilité : Cyrille peut prendre en charge des événements avec <strong className="text-foreground">peu de préavis grâce à son réseau et son expertise</strong>. Contactez-nous directement pour vérifier. La réactivité et le réseau sont un avantage majeur de Cygnature.
            </p>
          </div>
        ),
      },
      {
        question: "Comment se passe le suivi d'un projet entre les premiers échanges et le jour J ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Notre processus de suivi :
            </p>
            <ul className="space-y-2">
              {[
                'Points réguliers (téléphone, visio, présentiel)',
                'Accès à l\'avancement des réservations',
                'Validation étape par étape',
                'Communication proactive et transparente',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed italic">Notre philosophie : transparence totale et communication proactive.</p>
          </div>
        ),
      },
      {
        question: "Cygnature Production propose-t-elle des contrats et garanties ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Oui — <strong className="text-foreground">contrat de prestation pour chaque mission</strong> : périmètre clairement défini, conditions d'annulation, acompte et solde. Cygnature travaille avec des prestataires également liés par contrat. Sérieux et cadre juridique protecteur pour le client.
            </p>
          </div>
        ),
      },
      {
        question: "Que se passe-t-il si un imprévu survient le jour de l'événement ?",
        answer: (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              C'est la force du <strong className="text-foreground">« Ninja de l'événementiel »</strong>. Cyrille anticipe avec des plans B pour chaque point critique (météo, prestataire défaillant, retard). Présence physique le jour J garantie.
            </p>
            <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 flex items-start gap-3">
              <Shield size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">250+ événements = réactivité et sang-froid éprouvés.</strong> Rien ne nous surprend plus.
              </p>
            </div>
          </div>
        ),
      },
    ],
  },
];
